import React, { useState } from 'react';
import { Card, Badge, Collapse, Button } from 'react-bootstrap';
import type { CustomerSource } from '../api/generated/model';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { deleteCustomersSourcesSourceId } from '../api/generated/arrayscapeGamingDashboardAPI';
import { useNavigate } from 'react-router-dom';

interface CustomerSourceCardProps {
  source: CustomerSource;
  isPrimary: boolean;
  compact?: boolean;
  onDelete?: () => void;
  customerId?: string; // Customer ID to navigate away if customer is deleted
}

const CustomerSourceCard: React.FC<CustomerSourceCardProps> = ({ source, isPrimary, compact = false, onDelete, customerId }) => {
  const [isExpanded, setIsExpanded] = useState(!compact);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: () => deleteCustomersSourcesSourceId(source.id),
    onSuccess: () => {
      // Invalidate customer queries to refetch
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      if (onDelete) {
        onDelete();
      }
    },
    onError: (error: any) => {
      // If we get a 404 after deletion, it means the customer was deleted
      // Navigate back to customers list
      if (error?.response?.status === 404 && customerId) {
        navigate('/customers');
      }
    },
  });

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (window.confirm(`Are you sure you want to delete this internal customer source?\n\nThis action cannot be undone.`)) {
      deleteMutation.mutate();
    }
  };

  // Helper function to get source type badge variant and border color
  const getSourceTypeBorder = (sourceType: string): string => {
    return 'var(--badge-minecraft-text)';
  };

  const borderColor = getSourceTypeBorder(source.source_type);

  // Format address as a single line
  const formatAddress = () => {
    if (!source.address) return null;

    const parts = [
      source.address.line1,
      source.address.line2,
      source.address.city,
      [source.address.state, source.address.zip].filter(Boolean).join(' '),
    ].filter(Boolean);

    return parts.join(', ');
  };

  return (
    <Card
      className="source-card"
      style={{
        borderLeft: `4px solid ${borderColor}`,
      }}
    >
      <Card.Body className={compact ? 'p-2' : ''}>
        {/* Header */}
        <div
          className="d-flex justify-content-between align-items-start"
          style={{ cursor: compact ? 'pointer' : 'default' }}
          onClick={() => compact && setIsExpanded(!isExpanded)}
        >
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <Badge
              bg="success"
              style={{
                textTransform: 'capitalize',
                fontSize: compact ? '0.75rem' : '0.875rem',
              }}
            >
              {source.source_type}
            </Badge>
            {isPrimary && (
              <Badge bg="warning" text="dark" className={compact ? 'small' : ''}>
                <i className="bi bi-star-fill me-1"></i>
                PRIMARY
              </Badge>
            )}
            {source.dirty && (
              <Badge bg="danger" className={compact ? 'small' : ''}>
                <i className="bi bi-exclamation-circle me-1"></i>
                DIRTY
              </Badge>
            )}
          </div>
          <div className="d-flex align-items-center gap-2">
            {compact && (
              <i className={`bi bi-chevron-${isExpanded ? 'up' : 'down'} text-muted`}></i>
            )}
            {!compact && source.source_type === 'internal' && (
              <Button
                variant="outline-danger"
                size="sm"
                onClick={handleDelete}
                disabled={deleteMutation.isPending}
              >
                {deleteMutation.isPending ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                    Deleting...
                  </>
                ) : (
                  <>
                    <i className="bi bi-trash me-1"></i>
                    Delete
                  </>
                )}
              </Button>
            )}
          </div>
        </div>

        {/* Collapsed content (always visible when not compact) */}
        <Collapse in={isExpanded}>
          <div>
            {/* Name */}
            <h5 className={`${compact ? 'mt-2 mb-1' : 'mt-3 mb-3'}`}>{source.name}</h5>

            {/* Source IDs */}
            <div className="mb-2">
              <div className="small">
                <strong>Source ID:</strong> <code className="text-muted ms-2">{source.id}</code>
              </div>
              {source.source_integration_id && (
                <div className="small">
                  <strong>Integration ID:</strong> <code className="text-muted ms-2">{source.source_integration_id}</code>
                </div>
              )}
              {source.source_record_id && (
                <div className="small">
                  <strong>Customer ID:</strong> <code className="text-muted ms-2">{source.source_record_id}</code>
                </div>
              )}
            </div>

            {/* Contact Information */}
            {source.contact && (
              <div className="mb-2">
                <strong className="small">Contact:</strong>
                <div className="ms-3 small">
                  {source.contact.name && (
                    <div>
                      <i className="bi bi-person me-2 text-muted"></i>
                      {source.contact.name}
                    </div>
                  )}
                  {source.contact.email && (
                    <div>
                      <i className="bi bi-envelope me-2 text-muted"></i>
                      <a href={`mailto:${source.contact.email}`} className="text-decoration-none">
                        {source.contact.email}
                      </a>
                    </div>
                  )}
                  {source.contact.phone && (
                    <div>
                      <i className="bi bi-telephone me-2 text-muted"></i>
                      <a href={`tel:${source.contact.phone}`} className="text-decoration-none">
                        {source.contact.phone}
                      </a>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Address Information */}
            {source.address && formatAddress() && (
              <div className="mb-2">
                <strong className="small">Address:</strong>
                <div className="ms-3 small">
                  <i className="bi bi-geo-alt me-2 text-muted"></i>
                  {formatAddress()}
                </div>
              </div>
            )}

            {/* Timestamps */}
            {!compact && (
              <div className="small text-muted mt-3">
                {source.created_at && (
                  <div>Created: {new Date(source.created_at).toLocaleString()}</div>
                )}
                {source.updated_at && (
                  <div>Updated: {new Date(source.updated_at).toLocaleString()}</div>
                )}
              </div>
            )}
          </div>
        </Collapse>
      </Card.Body>
    </Card>
  );
};

export default CustomerSourceCard;
